var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { P, PCSS, CustomElementUtils, Utils, Components, PropertyConverters, CustomElement, Watch, stopPropagationHandler, DefaultComparer, PropertyChangeEventName, Debounce } from '@pacem/pacem-core';
import { FieldChangeEvent } from './types';
import { PacemFormRelevantElement } from './form';
const ORIGINAL_VALUE_FIELD = 'pacem:model:original-value';
export class PacemModelElement extends PacemFormRelevantElement {
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this._registerFieldConditionally();
        this._setAsOriginalValue(this.value);
    }
    _registerFieldConditionally() {
        const name = this.name, form = this.form;
        if (!Utils.isNullOrEmpty(name) && !Utils.isNull(form)
            && (!this.hasAttribute('autobind')
                || (this.getAttribute('autobind') !== 'off' && this.getAttribute('autobind') !== 'false'))) {
            form.registerField(name, this);
            // automatic bind value given the field name
            if (!this.hasAttribute('value')) {
                const formId = form.id = form.id || 'frm_' + Utils.uniqueCode();
                this.setAttribute('value', `{{ #${formId}.entity.${name}, twoway }}`);
            }
        }
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        switch (name) {
            case 'value':
                this.viewValue = this.getViewValue(val);
                break;
            case 'valid':
                if (!val) {
                    Utils.addClass(this, PCSS + '-invalid');
                }
                else {
                    Utils.removeClass(this, PCSS + '-invalid');
                }
                break;
            case 'dirty':
                if (val) {
                    Utils.addClass(this, PCSS + '-dirty');
                }
                else {
                    Utils.removeClass(this, PCSS + '-dirty');
                }
                break;
            case 'name':
                let form = this.form;
                if (form != null) {
                    if (old)
                        form.unregisterField(old);
                }
                this._registerFieldConditionally();
                break;
            case 'form':
                let n = this.name;
                if (!Utils.isNullOrEmpty(n)) {
                    if (old != null)
                        old.unregisterField(n);
                }
                this._registerFieldConditionally();
                break;
        }
    }
    disconnectedCallback() {
        let name = this.name, form = this.form;
        if (!Utils.isNullOrEmpty(name) && !Utils.isNull(form))
            form.unregisterField(name);
        super.disconnectedCallback();
    }
    get originalValue() {
        return CustomElementUtils.getAttachedPropertyValue(this, ORIGINAL_VALUE_FIELD);
    }
    _setAsOriginalValue(v) {
        CustomElementUtils.setAttachedPropertyValue(this, ORIGINAL_VALUE_FIELD, v);
    }
    reset() {
        this.value = this.originalValue;
        // then
        this.dirty = false;
    }
    setPristine() {
        this._setAsOriginalValue(this.value);
        //
        this.dirty = false;
    }
    setDirty() {
        this.dirty = true;
    }
}
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemModelElement.prototype, "viewValue", void 0);
__decorate([
    Watch({
        converter: {
            convert: (attr, element) => {
                if (!(element instanceof PacemModelElement)) {
                    return attr;
                }
                return element.convertValueAttributeToProperty(attr);
            },
            convertBack: (prop) => prop.toString()
        }
    })
], PacemModelElement.prototype, "value", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemModelElement.prototype, "dirty", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemModelElement.prototype, "valid", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemModelElement.prototype, "name", void 0);
export class PacemBaseElement extends PacemModelElement {
    constructor() {
        super(...arguments);
        this.focusHandler = (evt) => {
            const focusFieldgroup = (add = true) => {
                // TODO: re-think, since this is an ugly workaround just to properly set the outline and borders of the fieldgroup 
                if (Utils.hasClass(this.parentElement, '.' + PCSS + '-fieldgroup')) {
                    (add ? Utils.addClass : Utils.removeClass).apply(this, [this.parentElement, PCSS + '-focus']);
                }
            };
            const blurFieldgroup = () => focusFieldgroup(false);
            switch (evt.type) {
                case 'focus':
                    Utils.addClass(this, PCSS + '-focus');
                    focusFieldgroup();
                    this._preFocusValue = this.value;
                    break;
                case 'blur':
                    Utils.removeClass(this, PCSS + '-focus');
                    blurFieldgroup();
                    break;
            }
            if (evt.bubbles)
                this.emit(evt);
            else
                this.dispatchEvent(new FocusEvent(evt.type));
        };
        this.changeHandler = (evt) => {
            const val = this.value;
            this.onChange(evt).then(v => {
                if (!this.compareValuePropertyValues(val, v)) {
                    this.dispatchEvent(new FieldChangeEvent({
                        fieldName: this.name,
                        oldValue: val,
                        currentValue: v,
                        firstChange: !this.dirty
                    }));
                    this.dirty = true;
                }
            }, _ => {
                // do nothing
            });
        };
        this.keydownHandler = (evt) => {
            stopPropagationHandler(evt);
        };
        this.keyupHandler = (evt) => {
            switch (evt.keyCode) {
                case /* Esc */ 27:
                    stopPropagationHandler(evt);
                    // choose between reset and set
                    if (this.compareValuePropertyValues(this.originalValue, this._preFocusValue)) {
                        this.reset();
                    }
                    else {
                        this.value = this._preFocusValue;
                    }
                    this.blur();
                    break;
                case /* Enter */ 13:
                    if (this.preventKeyboardSubmit) {
                        stopPropagationHandler(evt);
                    }
                    break;
            }
        };
    }
    compareValuePropertyValues(old, val) {
        return DefaultComparer(old, val);
    }
    get preventKeyboardSubmit() {
        return false;
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        switch (name) {
            case 'required':
            case 'disabled':
            case 'autofocus':
            case 'placeholder':
            case 'readonly':
                for (let inputField of this.inputFields) {
                    val ? inputField.setAttribute(name, val) : inputField.removeAttribute(name);
                }
                break;
            //case 'name':
            case 'tabOrder':
                for (let inputField of this.inputFields) {
                    inputField.setAttribute('tabindex', val);
                }
                break;
            case 'value':
                this.acceptValue(val);
                break;
        }
        if (name === 'required'
            || name === 'disabled'
            || name === 'readonly') {
            let className = `${PCSS}-${name}`;
            val ? Utils.addClass(this, className) : Utils.removeClass(this, className);
        }
        if (name === 'readonly') {
            this.toggleReadonlyView(val);
        }
        // aria-... attributes
        else if (name === 'required') {
            this.aria.attributes.set('required', !!val + '');
        }
        else if (name === 'valid') {
            // Specs say that 'aria-invalid' on required attributes SHOULD NOT be set before submission, 
            // think about a 'submitted state'(?) to check against...
            this.aria.attributes.set('invalid', (!val && !!this.dirty) + '');
        }
    }
    registerInputFields(inputFields = this.inputFields) {
        if (!Utils.isNull(inputFields) && !Utils.isArray(inputFields)) {
            inputFields = [inputFields];
        }
        for (let inputField of inputFields) {
            inputField.addEventListener('focus', this.focusHandler, false);
            inputField.addEventListener('blur', this.focusHandler, false);
            inputField.addEventListener('change', this.changeHandler, false);
            // avoid interference with other handlers (e.g. cref UI.PacemAdapterElement)
            inputField.addEventListener('keydown', this.keydownHandler, false);
            inputField.addEventListener('keyup', this.keyupHandler, false);
            inputField.addEventListener('mousedown', stopPropagationHandler, false);
            inputField.addEventListener('touchstart', stopPropagationHandler, { passive: false, capture: false });
        }
    }
    unregisterInputFields(inputFields = this.inputFields) {
        if (!Utils.isNull(inputFields) && !Utils.isArray(inputFields)) {
            inputFields = [inputFields];
        }
        for (let inputField of inputFields) {
            if (Utils.isNull(inputField))
                continue;
            inputField.removeEventListener('touchstart', stopPropagationHandler, { capture: false });
            inputField.removeEventListener('keydown', this.keydownHandler, false);
            inputField.removeEventListener('keyup', this.keyupHandler, false);
            inputField.removeEventListener('mousedown', stopPropagationHandler, false);
            //
            inputField.removeEventListener('change', this.changeHandler, false);
            inputField.removeEventListener('focus', this.focusHandler, false);
            inputField.removeEventListener('blur', this.focusHandler, false);
        }
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this.toggleReadonlyView(this.readonly || false);
        // aria-attribute
        if (!Utils.isNullOrEmpty(this.id)) {
            const label = document.querySelector(`label[for=${this.id}]`);
            if (label) {
                if (label.id) {
                    this.aria.attributes.set('labelledby', label.id);
                }
                else {
                    this.aria.attributes.set('label', label.textContent);
                }
            }
            else {
                this.aria.attributes.remove('labelledby');
                this.aria.attributes.remove('label');
            }
            ;
        }
        // add handlers
        this.registerInputFields();
    }
    disconnectedCallback() {
        // remove handlers
        this.unregisterInputFields();
        super.disconnectedCallback();
    }
    focus() {
        const fields = this.inputFields;
        if (fields && fields.length) {
            fields[0].focus();
        }
    }
    blur() {
        const fields = this.inputFields;
        if (!Utils.isNullOrEmpty(fields)) {
            for (let field of fields) {
                field.blur();
            }
        }
    }
}
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemBaseElement.prototype, "required", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemBaseElement.prototype, "autofocus", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemBaseElement.prototype, "readonly", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemBaseElement.prototype, "name", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemBaseElement.prototype, "placeholder", void 0);
export class PacemItemElement extends Components.PacemElement {
    #container;
    /** @overridable */
    findContainer() {
        return CustomElementUtils.findAncestor(this, n => n instanceof PacemItemsContainerBaseElement);
    }
    get container() {
        return this.#container;
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        const container = this.#container = this.findContainer();
        if (!Utils.isNull(container)) {
            container.register(this);
        }
    }
    disconnectedCallback() {
        if (!Utils.isNull(this.#container)) {
            this.#container.unregister(this);
        }
        super.disconnectedCallback();
    }
}
export class PacemItemsContainerBaseElement extends PacemBaseElement {
    /**
     * Registers a new item among the items.
     * @param item {PacemDataItemElement} Item to be enrolled
     */
    register(item) {
        let flag = true;
        if (Utils.isNull(this.items)) {
            this.items = [item];
        }
        else if (this.items.indexOf(item) === -1) {
            this.items.push(item);
        }
        else {
            flag = false;
        }
        return flag;
    }
    /**
     * Removes an existing element from the items.
     * @param item {PacemDataItemElement} Item to be removed
     */
    unregister(item) {
        const ndx = !Utils.isNull(this.items) && this.items.indexOf(item);
        if (ndx >= 0) {
            this.items.splice(ndx, 1)[0];
            return true;
        }
        return false;
    }
}
__decorate([
    Watch( /* can only be databound or assigned at runtime */)
], PacemItemsContainerBaseElement.prototype, "items", void 0);
let PacemDataItemElement = class PacemDataItemElement extends PacemItemElement {
};
__decorate([
    Watch({
        converter: {
            convert: (attr, element) => {
                // boolean?
                if (attr === 'true')
                    return true;
                if (attr === 'false')
                    return false;
                // number?
                let num = parseFloat(attr);
                if (!isNaN(num))
                    return num;
                // date?
                let date = Utils.Dates.parse(attr);
                if (Utils.Dates.isDate(date))
                    return date;
                return attr;
            },
            convertBack: (prop) => prop.toString()
        }
    })
], PacemDataItemElement.prototype, "value", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemDataItemElement.prototype, "label", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemDataItemElement.prototype, "disabled", void 0);
PacemDataItemElement = __decorate([
    CustomElement({ tagName: P + '-data-item' })
], PacemDataItemElement);
export { PacemDataItemElement };
export class PacemDataSourceElement extends PacemItemsContainerBaseElement {
    constructor(multipleChoice = false) {
        super();
        this.multipleChoice = multipleChoice;
        this._itemPropertyChangedHandler = (evt) => {
            this.databind();
        };
    }
    /**
     * Registers a new item among the items.
     * @param item {PacemDataItemElement} Item to be enrolled
     */
    register(item) {
        const flag = super.register(item);
        if (flag) {
            item.addEventListener(PropertyChangeEventName, this._itemPropertyChangedHandler, false);
        }
        return flag;
    }
    /**
     * Removes an existing element from the items.
     * @param item {PacemDataItemElement} Item to be removed
     */
    unregister(item) {
        const flag = super.register(item);
        if (flag) {
            item.removeEventListener(PropertyChangeEventName, this._itemPropertyChangedHandler, false);
        }
        return flag;
    }
    buildAdaptedDatasource(ds = this.datasource) {
        return ds && ds.map(i => this.mapEntityToItem(i));
    }
    databind(datasource = this.buildAdaptedDatasource(this.datasource)) {
        if (Utils.isNull(datasource) && Utils.isNull(this.adaptedDatasource))
            return;
        const ds = this.adaptedDatasource = datasource || []; /*
            .map(i => this.mapEntityToItem(i))*/
        ;
        if (Utils.isNullOrEmpty(ds.filter(i => this.isDataSourceItemSelected(i))))
            this.handleDatasourceMismatch(ds);
    }
    handleDatasourceMismatch(datasource) {
        this.value = undefined;
    }
    /**
     * Checks wether the provided `item` matches the control's criteria for a selected option against a provided value (which defaults to control's value).
     * @param item {any} item to check against
     * @param value {any} value to match
     */
    isItemSelected(item, value = this.value) {
        if (Utils.isNullOrEmpty(value)) {
            return false;
        }
        const v = this.mapEntityToValue(item), c = this.compareBy;
        if (this.multipleChoice && Utils.isArray(value))
            return !Utils.isNull(value.find(j => 
            // caution: numbers and strings might be compared, ease the comparison by loosing equality constraints: `===` to `==`.
            j /* value item */ == /*=*/ v /* datasource item */ || (typeof j === 'object' && !Utils.isNullOrEmpty(c) && c in v && c in j && v[c] == /*=*/ j[c])));
        else if (!this.multipleChoice) {
            return value == /*=*/ v || (typeof value === 'object' && !Utils.isNullOrEmpty(c) && c in v && c in value && v[c] == /*=*/ value[c]);
        }
        else {
            return false;
        }
    }
    /**
     * Checks wether the provided `item` matches the control's criteria for a selected option against a provided value (which defaults to control's value).
     * @param item {DataSourceItem} item to check against
     * @param value {any} value to match
     */
    isDataSourceItemSelected(item, value = this.value) {
        if (Utils.isNullOrEmpty(value)) {
            return false;
        }
        if (item.disabled) {
            return false;
        }
        const c = this.compareBy;
        if (this.multipleChoice && Utils.isArray(value)) {
            // caution: numbers and strings might be compared, ease the comparison by loosing equality constraints: `===` to `==`.
            let found = value.find(j => j == /*=*/ item.value || (typeof j === 'object' && !Utils.isNullOrEmpty(c) && c in j && !Utils.isNullOrEmpty(item.value) && c in item.value && j[c] == /*=*/ item.value[c]));
            return !Utils.isNull(found);
        }
        else if (!this.multipleChoice && !Utils.isNullOrEmpty(item.value)) {
            return value == /*=*/ item.value || (typeof value === 'object' && !Utils.isNullOrEmpty(c) && c in value && !Utils.isNullOrEmpty(item.value) && c in item.value && value[c] == /*=*/ item.value[c]);
        }
        else {
            return false;
        }
    }
    mapEntityToValue(entity) {
        if (entity == null) {
            throw 'entity cannot be null';
        }
        // declared
        if (entity instanceof PacemDataItemElement) {
            return entity.value;
        }
        // databound
        let value = entity, prop;
        if (prop = this.valueProperty) {
            value = entity[prop];
        }
        return value;
    }
    mapEntityToViewValue(entity) {
        if (entity == null)
            throw 'entity cannot be null';
        // declared
        if (entity instanceof PacemDataItemElement) {
            return entity.label || entity.value;
        }
        // databound
        let viewValue = entity.toString(), prop;
        if (prop = this.textProperty) {
            viewValue = entity[prop];
        }
        return viewValue;
    }
    mapEntityToItem(entity) {
        if (entity == null) {
            throw 'entity cannot be null';
        }
        // declared
        if (entity instanceof PacemDataItemElement) {
            return { value: entity.value, viewValue: entity.label || entity.value, disabled: entity.disabled, data: entity };
        }
        // databound
        let disabled = false;
        const disabledProp = this.disabledProperty;
        if (!Utils.isNullOrEmpty(disabledProp)) {
            disabled = entity[disabledProp] || false;
        }
        return { value: this.mapEntityToValue(entity), viewValue: this.mapEntityToViewValue(entity), disabled: disabled, data: entity };
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        switch (name) {
            case 'datasource':
            case 'textProperty':
            case 'disabledProperty':
            case 'valueProperty':
                cancelAnimationFrame(this._handle);
                this._handle = requestAnimationFrame(() => {
                    this.databind();
                    // refresh view value, since it depends on selected items.
                    this.viewValue = this.getViewValue(this.value);
                });
                break;
            case 'items':
                cancelAnimationFrame(this._handleItems);
                this._handleItems = requestAnimationFrame(() => {
                    this.datasource = this.items;
                });
                break;
        }
    }
    getViewValue(val) {
        if (Utils.isNullOrEmpty(this.datasource)) {
            return undefined;
        }
        return this.datasource.filter(i => this.isItemSelected(i)).map(i => this.mapEntityToViewValue(i)).join(', ');
    }
    convertValueAttributeToProperty(attr) {
        if (this.multipleChoice) {
            return attr.split(',').map(i => i.trim());
        }
        return attr;
    }
    compareValuePropertyValues(old, val) {
        return DefaultComparer(old, val);
    }
}
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json })
], PacemDataSourceElement.prototype, "datasource", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemDataSourceElement.prototype, "valueProperty", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemDataSourceElement.prototype, "textProperty", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemDataSourceElement.prototype, "disabledProperty", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemDataSourceElement.prototype, "compareBy", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Json })
], PacemDataSourceElement.prototype, "adaptedDatasource", void 0);
__decorate([
    Debounce(true)
], PacemDataSourceElement.prototype, "databind", null);
